import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import * as THREE from 'three';
import { SimulatorHotspot, SimulatorHotspotAction } from '../../types';
import { coords2DtoSpherical, sphericalTo2DCoords } from '../../data/clinicalEnvironments';
import { useThemeTokens } from '../../hooks/useActiveTheme';
import { 
  Compass, 
  RotateCcw, 
  ZoomIn, 
  ZoomOut, 
  Play, 
  Pause, 
  Crosshair, 
  ArrowLeft, 
  ArrowRight, 
  ArrowUp, 
  ArrowDown, 
  Sparkles,
  Layers,
  ChevronRight,
  Info,
  Volume2,
  VolumeX,
  SkipBack,
  SkipForward,
  X,
  CheckCircle2,
  Headphones,
  ListOrdered,
  Eye,
  Smartphone,
  Maximize,
  Minimize,
  Check
} from 'lucide-react';

interface ClinicalPhotosphereStageProps {
  panoramaUrl: string;
  fallbackImageUrl?: string;
  hotspots: SimulatorHotspot[];
  activeHotspotId: string | null;
  onSelectHotspot: (hotspot: SimulatorHotspot) => void;
  isCalibrating?: boolean;
  calibratingHotspotId?: string | null;
  onCalibrateHotspot?: (
    hotspotId: string, 
    spherical: { yaw: number; pitch: number }, 
    flat: { x: number; y: number }
  ) => void;
  initialHeading?: number;
  environmentName?: string;
  onNudgePin?: (dYaw: number, dPitch: number) => void;
  completedActionIds?: string[];
  onFallbackTo2D?: () => void;
  skillTitle?: string;
  onExecuteAction?: (action: SimulatorHotspotAction) => void;
  autoStartTour?: boolean;
}

interface TourStep {
  stepNumber: number;
  hotspot: SimulatorHotspot;
  action: SimulatorHotspotAction;
  cleanTitle: string;
  feedbackDialogue: string;
  category: string;
  isCritical: boolean;
  yaw: number;
  pitch: number;
}

interface ProjectedHotspot {
  hotspot: SimulatorHotspot;
  screenX: number;
  screenY: number;
  isVisible: boolean;
  isInFront: boolean;
  angleFromCenter: number;
  yaw: number;
  pitch: number;
}

export const ClinicalPhotosphereStage: React.FC<ClinicalPhotosphereStageProps> = ({
  panoramaUrl,
  fallbackImageUrl = '/images/environments/bedside_room.jpg',
  hotspots,
  activeHotspotId,
  onSelectHotspot,
  isCalibrating = false,
  calibratingHotspotId = null,
  onCalibrateHotspot,
  initialHeading = 0,
  environmentName = 'Clinical Room',
  onNudgePin,
  completedActionIds = [],
  onFallbackTo2D,
  skillTitle,
  onExecuteAction,
  autoStartTour = false
}) => {
  const theme = useThemeTokens();
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasMountRef = useRef<HTMLDivElement>(null);

  // Keep latest hotspots in ref so render loop doesn't trigger scene recreation
  const hotspotsRef = useRef(hotspots);
  hotspotsRef.current = hotspots;

  // Cached active texture ref
  const activeTextureRef = useRef<THREE.Texture | null>(null);

  // Three.js instances ref
  const threeRef = useRef<{
    scene: THREE.Scene;
    camera: THREE.PerspectiveCamera;
    renderer: THREE.WebGLRenderer;
    sphereMesh: THREE.Mesh;
    textureLoader: THREE.TextureLoader;
    raycaster: THREE.Raycaster;
    mouseVector: THREE.Vector2;
  } | null>(null);

  // Camera angles & animation state
  const cameraAnglesRef = useRef({
    lon: initialHeading,
    lat: 0,
    targetLon: initialHeading,
    targetLat: 0,
    isDragging: false,
    prevX: 0,
    prevY: 0,
    autoRotate: false,
    fov: 75,
    targetFov: 75
  });

  // UI state
  const [currentHeading, setCurrentHeading] = useState(initialHeading);
  const [currentPitch, setCurrentPitch] = useState(0);
  const [isAutoRotating, setIsAutoRotating] = useState(false);
  const [isLoadingTexture, setIsLoadingTexture] = useState(true);
  const [textureError, setTextureError] = useState<string | null>(null);
  const [projectedHotspots, setProjectedHotspots] = useState<ProjectedHotspot[]>([]);
  const [touchDistance, setTouchDistance] = useState<number | null>(null);

  // Guided Skill Tour & Audio Read-Aloud State
  const [isTourActive, setIsTourActive] = useState(false);
  const [tourStepIndex, setTourStepIndex] = useState(0);
  const [isVoiceEnabled, setIsVoiceEnabled] = useState(true);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isTourPaused, setIsTourPaused] = useState(false);
  const [tourSpeed, setTourSpeed] = useState<1 | 1.25>(1);
  const [showTourDiscoveryPill, setShowTourDiscoveryPill] = useState(true);
  const [showTourPreview, setShowTourPreview] = useState(false);
  const tourPreviewRef = useRef<HTMLDivElement>(null);
  const stageWrapperRef = useRef<HTMLDivElement>(null);

  // Mobile Gyroscope VR Motion & Fullscreen State
  const [isGyroActive, setIsGyroActive] = useState(false);
  const [gyroSupported, setGyroSupported] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const initialGyroHeadingRef = useRef<number | null>(null);

  const tourTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isTourActiveRef = useRef(false);
  isTourActiveRef.current = isTourActive;
  const isTourPausedRef = useRef(false);
  isTourPausedRef.current = isTourPaused;
  const tourStepIndexRef = useRef(0);
  tourStepIndexRef.current = tourStepIndex;
  const isMountedRef = useRef(true);
  const activeUtteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

  // Robustly cancel speech synthesis and strip callbacks so no ghost timers fire
  const cancelSpeech = useCallback(() => {
    if (tourTimerRef.current) {
      clearTimeout(tourTimerRef.current);
      tourTimerRef.current = null;
    }
    if (activeUtteranceRef.current) {
      activeUtteranceRef.current.onstart = null;
      activeUtteranceRef.current.onend = null;
      activeUtteranceRef.current.onerror = null;
      activeUtteranceRef.current = null;
    }
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
    setIsSpeaking(false);
  }, []);

  // Compile and sequence all procedural steps chronologically across all stations
  const tourSteps: TourStep[] = useMemo(() => {
    const list: TourStep[] = [];
    (hotspots || []).forEach(hs => {
      const coords = hs.sphericalCoordinates || coords2DtoSpherical(
        hs.coordinates?.x ?? 50, 
        hs.coordinates?.y ?? 50
      );
      (hs.availableActions || []).forEach(act => {
        const match = act.label.match(/^step\s*(\d+)[:\s-]*(.*)/i);
        const parsedNum = match ? parseInt(match[1], 10) : 999;
        const cleanTitle = match && match[2] ? match[2].trim() : act.label;

        list.push({
          stepNumber: parsedNum,
          hotspot: hs,
          action: act,
          cleanTitle,
          feedbackDialogue: act.feedbackDialogue || 'Execute this action in accordance with state board protocol.',
          category: act.category || 'technique',
          isCritical: Boolean(act.isCritical),
          yaw: Number.isFinite(coords.yaw) ? coords.yaw : 0,
          pitch: Number.isFinite(coords.pitch) ? coords.pitch : 0
        });
      });
    });

    // Sort by extracted step number ascending
    list.sort((a, b) => a.stepNumber - b.stepNumber);

    // Re-index cleanly 1..N
    return list.map((step, idx) => ({
      ...step,
      stepNumber: idx + 1
    }));
  }, [hotspots]);

  // Unique chronological stations for Tour Preview itinerary
  const uniqueStations = useMemo(() => {
    const seen = new Set<string>();
    const stations: { id: string; name: string; stepCount: number; isCritical: boolean }[] = [];
    tourSteps.forEach(step => {
      if (!seen.has(step.hotspot.id)) {
        seen.add(step.hotspot.id);
        const count = tourSteps.filter(s => s.hotspot.id === step.hotspot.id).length;
        const hasCritical = tourSteps.some(s => s.hotspot.id === step.hotspot.id && s.isCritical);
        stations.push({
          id: step.hotspot.id,
          name: step.hotspot.name || 'Station',
          stepCount: count,
          isCritical: hasCritical
        });
      }
    });
    return stations;
  }, [tourSteps]);

  // Click outside to close tour preview popover
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (tourPreviewRef.current && !tourPreviewRef.current.contains(e.target as Node)) {
        setShowTourPreview(false);
      }
    };
    if (showTourPreview) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTourPreview]);

  // Speech synthesis audio read-aloud for active step
  const speakTourStep = useCallback((step: TourStep) => {
    cancelSpeech();

    if (!isMountedRef.current || !isTourActiveRef.current || isTourPausedRef.current) {
      return;
    }

    if (!isVoiceEnabled) {
      setIsSpeaking(false);
      if (tourTimerRef.current) clearTimeout(tourTimerRef.current);
      tourTimerRef.current = setTimeout(() => {
        if (isMountedRef.current && isTourActiveRef.current && !isTourPausedRef.current) {
          handleNextTourStep();
        }
      }, 5000 / tourSpeed);
      return;
    }

    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;

    const textToSpeak = `Step ${step.stepNumber}: ${step.cleanTitle}. ${step.feedbackDialogue}`;
    const utterance = new SpeechSynthesisUtterance(textToSpeak);
    utterance.rate = 0.95 * tourSpeed;
    utterance.pitch = 1.0;

    const voices = window.speechSynthesis.getVoices();
    const naturalVoice = voices.find(v => 
      v.lang.startsWith('en') && (v.name.includes('Google') || v.name.includes('Natural') || v.name.includes('Samantha') || v.name.includes('Zira') || v.name.includes('Victoria'))
    ) || voices.find(v => v.lang.startsWith('en'));
    if (naturalVoice) {
      utterance.voice = naturalVoice;
    }

    activeUtteranceRef.current = utterance;

    utterance.onstart = () => {
      if (isMountedRef.current && isTourActiveRef.current) {
        setIsSpeaking(true);
      }
    };

    utterance.onend = () => {
      if (activeUtteranceRef.current === utterance) {
        activeUtteranceRef.current = null;
      }
      setIsSpeaking(false);
      if (!isMountedRef.current || !isTourActiveRef.current || isTourPausedRef.current) {
        return;
      }
      if (tourTimerRef.current) clearTimeout(tourTimerRef.current);
      tourTimerRef.current = setTimeout(() => {
        if (isMountedRef.current && isTourActiveRef.current && !isTourPausedRef.current) {
          handleNextTourStep();
        }
      }, 1600 / tourSpeed);
    };

    utterance.onerror = (e) => {
      if (activeUtteranceRef.current === utterance) {
        activeUtteranceRef.current = null;
      }
      setIsSpeaking(false);
      // If utterance was canceled, interrupted, or component/tour is no longer active, stop immediately!
      if (
        !isMountedRef.current ||
        !isTourActiveRef.current ||
        isTourPausedRef.current ||
        e.error === 'canceled' ||
        e.error === 'interrupted'
      ) {
        return;
      }
      if (tourTimerRef.current) clearTimeout(tourTimerRef.current);
      tourTimerRef.current = setTimeout(() => {
        if (isMountedRef.current && isTourActiveRef.current && !isTourPausedRef.current) {
          handleNextTourStep();
        }
      }, 2500 / tourSpeed);
    };

    window.speechSynthesis.speak(utterance);
  }, [cancelSpeech, isVoiceEnabled, tourSpeed]);

  // Navigate to specific step in tour
  const navigateToTourStep = useCallback((index: number) => {
    if (!isMountedRef.current || !tourSteps || tourSteps.length === 0) return;
    const clamped = Math.max(0, Math.min(tourSteps.length - 1, index));
    setTourStepIndex(clamped);
    tourStepIndexRef.current = clamped;

    const step = tourSteps[clamped];
    if (!step) return;

    // Smoothly rotate 360 camera to station's spherical coordinates
    cameraAnglesRef.current.targetLon = step.yaw;
    cameraAnglesRef.current.targetLat = step.pitch;
    cameraAnglesRef.current.targetFov = 68; // Focus zoom

    // Highlight active hotspot
    onSelectHotspot(step.hotspot);

    // Speak / display narration
    if (!isTourPausedRef.current && isTourActiveRef.current) {
      speakTourStep(step);
    }
  }, [tourSteps, onSelectHotspot, speakTourStep]);

  const handleNextTourStep = () => {
    if (!isMountedRef.current || !isTourActiveRef.current) return;
    if (tourStepIndexRef.current < tourSteps.length - 1) {
      navigateToTourStep(tourStepIndexRef.current + 1);
    } else {
      // Completed full tour!
      setIsTourActive(false);
      isTourActiveRef.current = false;
      cancelSpeech();
      if (isVoiceEnabled && isMountedRef.current && typeof window !== 'undefined' && 'speechSynthesis' in window) {
        const doneUtt = new SpeechSynthesisUtterance("Guided walkthrough complete. You can now practice this skill independently!");
        doneUtt.rate = 1.0;
        activeUtteranceRef.current = doneUtt;
        doneUtt.onend = () => {
          if (activeUtteranceRef.current === doneUtt) activeUtteranceRef.current = null;
          setIsSpeaking(false);
        };
        doneUtt.onerror = () => {
          if (activeUtteranceRef.current === doneUtt) activeUtteranceRef.current = null;
          setIsSpeaking(false);
        };
        window.speechSynthesis.speak(doneUtt);
      }
    }
  };

  const handlePrevTourStep = () => {
    if (tourStepIndexRef.current > 0) {
      navigateToTourStep(tourStepIndexRef.current - 1);
    }
  };

  const handleStartTour = () => {
    if (!isMountedRef.current || tourSteps.length === 0) return;
    setShowTourDiscoveryPill(false);
    setIsTourActive(true);
    isTourActiveRef.current = true;
    setIsTourPaused(false);
    isTourPausedRef.current = false;
    navigateToTourStep(0);
  };

  // Automatically start tour if requested from parent (e.g. SkillsLab Play Skill Tour button)
  useEffect(() => {
    if (autoStartTour && tourSteps.length > 0 && !isTourActive) {
      const timer = setTimeout(() => {
        if (isMountedRef.current) {
          handleStartTour();
        }
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [autoStartTour, tourSteps.length]);

  const handlePauseResumeTour = () => {
    if (isTourPaused) {
      // Resume
      setIsTourPaused(false);
      isTourPausedRef.current = false;
      const step = tourSteps[tourStepIndex];
      if (step) {
        speakTourStep(step);
      }
    } else {
      // Pause
      setIsTourPaused(true);
      isTourPausedRef.current = true;
      cancelSpeech();
    }
  };

  const handleStopTour = () => {
    setIsTourActive(false);
    isTourActiveRef.current = false;
    setIsTourPaused(false);
    isTourPausedRef.current = false;
    cancelSpeech();
    cameraAnglesRef.current.targetFov = 75;
  };

  const toggleVoice = () => {
    const next = !isVoiceEnabled;
    setIsVoiceEnabled(next);
    if (!next) {
      cancelSpeech();
    } else if (isTourActive && !isTourPaused) {
      const step = tourSteps[tourStepIndex];
      if (step) {
        speakTourStep(step);
      }
    }
  };

  // Stop speech and pause tour when switching browser tabs or minimizing
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        cancelSpeech();
        if (isTourActiveRef.current) {
          setIsTourPaused(true);
          isTourPausedRef.current = true;
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handleVisibilityChange);
    };
  }, [cancelSpeech]);

  // Stop speech cleanly on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      isTourActiveRef.current = false;
      isTourPausedRef.current = true;
      cancelSpeech();
    };
  }, [cancelSpeech]);

  // Initialize Three.js Scene ONCE on mount
  useEffect(() => {
    if (!canvasMountRef.current || !containerRef.current) return;
    const container = containerRef.current;
    const canvasMount = canvasMountRef.current;
    const width = container.clientWidth || 800;
    const height = container.clientHeight || 450;

    let renderer: THREE.WebGLRenderer | null = null;
    let geometry: THREE.SphereGeometry | null = null;
    let material: THREE.MeshBasicMaterial | null = null;

    try {
      // Create WebGLRenderer directly without conflicting pre-calls to getContext
      renderer = new THREE.WebGLRenderer({
        antialias: true,
        powerPreference: 'default'
      });
      renderer.setSize(width, height);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      renderer.domElement.className = "w-full h-full block";

      // Clear any prior canvas elements and mount fresh Three.js canvas
      canvasMount.innerHTML = '';
      canvasMount.appendChild(renderer.domElement);

      geometry = new THREE.SphereGeometry(500, 60, 40);
      geometry.scale(-1, 1, 1);

      material = new THREE.MeshBasicMaterial({
        map: activeTextureRef.current || null,
        color: activeTextureRef.current ? 0xffffff : 0x1e1b4b,
        side: THREE.DoubleSide
      });
    } catch (webglErr: any) {
      console.warn("WebGL initialization failed in ClinicalPhotosphereStage, requesting 2D fallback:", webglErr);
      setTextureError("WebGL 3D graphics is not available in your current browser session. Switching seamlessly to High-Fidelity 2D Canvas.");
      setIsLoadingTexture(false);
      if (onFallbackTo2D) {
        onFallbackTo2D();
      }
      return;
    }

    // Scene & Camera
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, width / height, 1, 2000);
    camera.target = new THREE.Vector3(0, 0, 0);

    const sphereMesh = new THREE.Mesh(geometry, material);
    scene.add(sphereMesh);

    const textureLoader = new THREE.TextureLoader();
    textureLoader.setCrossOrigin('anonymous');

    const raycaster = new THREE.Raycaster();
    const mouseVector = new THREE.Vector2();

    threeRef.current = {
      scene,
      camera,
      renderer,
      sphereMesh,
      textureLoader,
      raycaster,
      mouseVector
    };

    let animationFrameId: number;
    let lastRenderLon = cameraAnglesRef.current.lon;
    let lastRenderLat = cameraAnglesRef.current.lat;
    let lastRenderFov = cameraAnglesRef.current.fov;
    let lastHotspotsCount = -1;

    // Animation / Render Loop
    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);

      const angles = cameraAnglesRef.current;
      if (!threeRef.current) return;

      // Auto rotation when enabled and not dragging
      if (angles.autoRotate && !angles.isDragging) {
        angles.targetLon += 0.08;
      }

      // Smooth interpolation (damping)
      angles.lon += (angles.targetLon - angles.lon) * 0.12;
      angles.lat += (angles.targetLat - angles.lat) * 0.12;
      angles.fov += (angles.targetFov - angles.fov) * 0.12;

      // Clamp vertical pitch to prevent gimbal flip
      angles.lat = Math.max(-85, Math.min(85, angles.lat));
      angles.targetLat = Math.max(-85, Math.min(85, angles.targetLat));

      // Clamp FOV strictly
      angles.fov = Math.max(35, Math.min(90, angles.fov));
      angles.targetFov = Math.max(35, Math.min(90, angles.targetFov));

      // Update camera FOV
      if (Math.abs(camera.fov - angles.fov) > 0.01) {
        camera.fov = angles.fov;
        camera.updateProjectionMatrix();
      }

      // Spherical coordinates -> Cartesian lookAt target
      const phi = THREE.MathUtils.degToRad(90 - angles.lat);
      const theta = THREE.MathUtils.degToRad(angles.lon);

      const targetX = 500 * Math.sin(phi) * Math.cos(theta);
      const targetY = 500 * Math.cos(phi);
      const targetZ = 500 * Math.sin(phi) * Math.sin(theta);

      camera.lookAt(targetX, targetY, targetZ);
      renderer.render(scene, camera);

      // Only re-calculate projected hotspots if camera moved significantly or hotspots changed
      const currentHotspots = hotspotsRef.current || [];
      const hasMoved = 
        Math.abs(angles.lon - lastRenderLon) > 0.04 ||
        Math.abs(angles.lat - lastRenderLat) > 0.04 ||
        Math.abs(angles.fov - lastRenderFov) > 0.04;

      if (hasMoved || lastHotspotsCount !== currentHotspots.length) {
        lastRenderLon = angles.lon;
        lastRenderLat = angles.lat;
        lastRenderFov = angles.fov;
        lastHotspotsCount = currentHotspots.length;

        const currentWidth = container.clientWidth || 800;
        const currentHeight = container.clientHeight || 450;
        const newProjected: ProjectedHotspot[] = [];

        for (const hs of currentHotspots) {
          const coords = hs.sphericalCoordinates || coords2DtoSpherical(
            hs.coordinates?.x ?? 50, 
            hs.coordinates?.y ?? 50
          );

          const coordsYaw = Number.isFinite(coords?.yaw) ? coords.yaw : 0;
          const coordsPitch = Number.isFinite(coords?.pitch) ? coords.pitch : 0;

          const hPhi = THREE.MathUtils.degToRad(90 - coordsPitch);
          const hTheta = THREE.MathUtils.degToRad(coordsYaw);

          const hPos = new THREE.Vector3(
            480 * Math.sin(hPhi) * Math.cos(hTheta),
            480 * Math.cos(hPhi),
            480 * Math.sin(hPhi) * Math.sin(hTheta)
          );

          const projected = hPos.clone().project(camera);
          const isInFront = projected.z < 1;
          const screenX = ((projected.x + 1) / 2) * currentWidth;
          const screenY = ((-projected.y + 1) / 2) * currentHeight;

          // Is it inside the visible viewport bounds?
          const isVisible = isInFront && 
            screenX >= -40 && 
            screenX <= currentWidth + 40 && 
            screenY >= -40 && 
            screenY <= currentHeight + 40;

          newProjected.push({
            hotspot: hs,
            screenX: Math.round(screenX),
            screenY: Math.round(screenY),
            isVisible,
            isInFront,
            angleFromCenter: Math.atan2(screenY - currentHeight / 2, screenX - currentWidth / 2),
            yaw: coordsYaw,
            pitch: coordsPitch
          });
        }

        setProjectedHotspots(newProjected);

        // Update UI heading indicators periodically
        const normHeading = Math.round(((angles.lon % 360) + 360) % 360);
        setCurrentHeading(normHeading > 180 ? normHeading - 360 : normHeading);
        setCurrentPitch(Math.round(angles.lat));
      }
    };

    animate();

    // Resize handler for window and container
    const handleResize = () => {
      if (!container || !threeRef.current) return;
      const w = container.clientWidth || 800;
      const h = container.clientHeight || 450;
      if (w === 0 || h === 0) return;
      threeRef.current.camera.aspect = w / h;
      threeRef.current.camera.updateProjectionMatrix();
      threeRef.current.renderer.setSize(w, h);
    };

    window.addEventListener('resize', handleResize);

    // Native non-passive Wheel listener to prevent page scroll and zoom cleanly
    const handleNativeWheel = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const zoomStep = Math.sign(e.deltaY) * 4;
      cameraAnglesRef.current.targetFov = Math.max(
        35, 
        Math.min(90, cameraAnglesRef.current.targetFov + zoomStep)
      );
    };

    container.addEventListener('wheel', handleNativeWheel, { passive: false });

    // ResizeObserver for container size shifts
    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        handleResize();
      });
      resizeObserver.observe(container);
    }

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', handleResize);
      container.removeEventListener('wheel', handleNativeWheel);
      if (resizeObserver) resizeObserver.disconnect();
      try {
        if (renderer) {
          renderer.dispose();
          if (renderer.domElement && renderer.domElement.parentNode) {
            renderer.domElement.parentNode.removeChild(renderer.domElement);
          }
        }
        if (geometry) geometry.dispose();
        if (material) material.dispose();
        if (activeTextureRef.current) {
          activeTextureRef.current.dispose();
          activeTextureRef.current = null;
        }
      } catch (e) {}
      threeRef.current = null;
    };
  }, []); // Run ONCE on mount - hotspotsRef handles dynamic hotspot updates without re-creating scene!

  // Load / Update 360 Texture
  useEffect(() => {
    if (!threeRef.current) return;
    setIsLoadingTexture(true);
    setTextureError(null);

    const targetUrl = panoramaUrl || fallbackImageUrl;
    const { sphereMesh, textureLoader } = threeRef.current;

    textureLoader.load(
      targetUrl,
      (texture) => {
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.generateMipmaps = true;
        texture.minFilter = THREE.LinearMipmapLinearFilter;
        activeTextureRef.current = texture;

        const newMaterial = new THREE.MeshBasicMaterial({
          map: texture,
          color: 0xffffff,
          side: THREE.DoubleSide
        });

        if (Array.isArray(sphereMesh.material)) {
          sphereMesh.material.forEach(m => m.dispose());
        } else {
          sphereMesh.material.dispose();
        }

        sphereMesh.material = newMaterial;
        setIsLoadingTexture(false);
      },
      undefined,
      (err) => {
        console.warn('360 Photosphere texture load warning, trying fallback:', err);
        // Fallback to secondary image
        if (targetUrl !== fallbackImageUrl) {
          textureLoader.load(
            fallbackImageUrl,
            (fallbackTex) => {
              fallbackTex.colorSpace = THREE.SRGBColorSpace;
              activeTextureRef.current = fallbackTex;
              sphereMesh.material = new THREE.MeshBasicMaterial({
                map: fallbackTex,
                color: 0xffffff,
                side: THREE.DoubleSide
              });
              setIsLoadingTexture(false);
            },
            undefined,
            () => {
              setTextureError('Failed to load 360 panorama image');
              setIsLoadingTexture(false);
            }
          );
        } else {
          setTextureError('Failed to load 360 panorama image');
          setIsLoadingTexture(false);
        }
      }
    );
  }, [panoramaUrl, fallbackImageUrl]);

  // Mouse / Touch Drag Controls
  const handleMouseDown = (e: React.MouseEvent) => {
    // Only drag with primary mouse button
    if (e.button !== 0) return;
    cameraAnglesRef.current.isDragging = true;
    cameraAnglesRef.current.prevX = e.clientX;
    cameraAnglesRef.current.prevY = e.clientY;
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!cameraAnglesRef.current.isDragging) return;
    const dx = e.clientX - cameraAnglesRef.current.prevX;
    const dy = e.clientY - cameraAnglesRef.current.prevY;
    cameraAnglesRef.current.prevX = e.clientX;
    cameraAnglesRef.current.prevY = e.clientY;

    const sensitivity = 0.18;
    cameraAnglesRef.current.targetLon -= dx * sensitivity;
    cameraAnglesRef.current.targetLat += dy * sensitivity;
  };

  const handleMouseUp = () => {
    cameraAnglesRef.current.isDragging = false;
  };

  // Touch handlers for mobile/tablet 360 navigation
  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      cameraAnglesRef.current.isDragging = true;
      cameraAnglesRef.current.prevX = e.touches[0].clientX;
      cameraAnglesRef.current.prevY = e.touches[0].clientY;
    } else if (e.touches.length === 2) {
      // 2-finger pinch zoom
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      setTouchDistance(dist);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.cancelable) {
      e.preventDefault();
    }
    if (e.touches.length === 1 && cameraAnglesRef.current.isDragging) {
      const dx = e.touches[0].clientX - cameraAnglesRef.current.prevX;
      const dy = e.touches[0].clientY - cameraAnglesRef.current.prevY;
      cameraAnglesRef.current.prevX = e.touches[0].clientX;
      cameraAnglesRef.current.prevY = e.touches[0].clientY;

      const sensitivity = 0.24;
      cameraAnglesRef.current.targetLon -= dx * sensitivity;
      cameraAnglesRef.current.targetLat += dy * sensitivity;
    } else if (e.touches.length === 2 && touchDistance !== null) {
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      const delta = touchDistance - dist;
      cameraAnglesRef.current.targetFov = Math.max(
        40, 
        Math.min(85, cameraAnglesRef.current.targetFov + delta * 0.1)
      );
      setTouchDistance(dist);
    }
  };

  const handleTouchEnd = () => {
    cameraAnglesRef.current.isDragging = false;
    setTouchDistance(null);
  };

  // Detect DeviceOrientation capability for Mobile Gyroscope VR Motion Look
  useEffect(() => {
    if (typeof window !== 'undefined' && 'DeviceOrientationEvent' in window) {
      setGyroSupported(true);
    }
  }, []);

  const toggleGyro = async () => {
    if (!isGyroActive) {
      // iOS 13+ permission request
      if (typeof (DeviceOrientationEvent as any)?.requestPermission === 'function') {
        try {
          const res = await (DeviceOrientationEvent as any).requestPermission();
          if (res !== 'granted') return;
        } catch (err) {
          console.warn('Device orientation permission rejected:', err);
          return;
        }
      }
      initialGyroHeadingRef.current = null;
      setIsGyroActive(true);
    } else {
      setIsGyroActive(false);
    }
  };

  // Gyroscope Motion Look Listener
  useEffect(() => {
    if (!isGyroActive) return;

    const handleOrientation = (e: DeviceOrientationEvent) => {
      if (e.alpha === null || e.beta === null) return;
      if (initialGyroHeadingRef.current === null) {
        initialGyroHeadingRef.current = e.alpha - cameraAnglesRef.current.lon;
      }
      const baseLon = e.alpha - (initialGyroHeadingRef.current || 0);
      // Beta angle: phone vertical tilt (holding naturally ~50 deg)
      const targetPitch = Math.max(-80, Math.min(80, (e.beta || 0) - 55));
      cameraAnglesRef.current.targetLon = baseLon;
      cameraAnglesRef.current.targetLat = targetPitch;
    };

    window.addEventListener('deviceorientation', handleOrientation);
    return () => {
      window.removeEventListener('deviceorientation', handleOrientation);
    };
  }, [isGyroActive]);

  // Fullscreen Viewport Mode for Mobile & Desktop
  const toggleFullscreen = () => {
    const el = stageWrapperRef.current;
    if (!el) return;

    if (!document.fullscreenElement) {
      if (el.requestFullscreen) {
        el.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => {});
      } else if ((el as any).webkitRequestFullscreen) {
        (el as any).webkitRequestFullscreen();
        setIsFullscreen(true);
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => {});
      } else if ((document as any).webkitExitFullscreen) {
        (document as any).webkitExitFullscreen();
        setIsFullscreen(false);
      }
    }
  };

  useEffect(() => {
    const handleFs = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };
    document.addEventListener('fullscreenchange', handleFs);
    document.addEventListener('webkitfullscreenchange', handleFs);
    return () => {
      document.removeEventListener('fullscreenchange', handleFs);
      document.removeEventListener('webkitfullscreenchange', handleFs);
    };
  }, []);

  // Click on 3D Sphere in Super Admin Calibration Mode
  const handleStageClick = (e: React.MouseEvent) => {
    if (!isCalibrating || !calibratingHotspotId || !onCalibrateHotspot || !threeRef.current || !containerRef.current) {
      return;
    }

    const rect = containerRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

    const { camera, sphereMesh, raycaster } = threeRef.current;
    raycaster.setFromCamera(new THREE.Vector2(x, y), camera);
    const intersects = raycaster.intersectObject(sphereMesh);

    if (intersects.length > 0) {
      const p = intersects[0].point;
      const r = Math.sqrt(p.x * p.x + p.y * p.y + p.z * p.z);
      // Spherical coordinate math
      const pitchDeg = Math.round(Math.asin(p.y / r) * (180 / Math.PI));
      const yawDeg = Math.round(Math.atan2(p.z, p.x) * (180 / Math.PI));

      const flatCoords = sphericalTo2DCoords(yawDeg, pitchDeg);
      onCalibrateHotspot(calibratingHotspotId, { yaw: yawDeg, pitch: pitchDeg }, flatCoords);
    }
  };

  // Quick camera orientations
  const rotateToAngle = (targetLon: number, targetLat = 0) => {
    cameraAnglesRef.current.targetLon = targetLon;
    cameraAnglesRef.current.targetLat = targetLat;
  };

  // Look directly at a specific hotspot
  const lookAtHotspot = (hs: SimulatorHotspot) => {
    const coords = hs.sphericalCoordinates || coords2DtoSpherical(
      hs.coordinates?.x ?? 50, 
      hs.coordinates?.y ?? 50
    );
    rotateToAngle(coords.yaw, coords.pitch);
  };

  const toggleAutoRotate = () => {
    const next = !isAutoRotating;
    setIsAutoRotating(next);
    cameraAnglesRef.current.autoRotate = next;
  };

  return (
    <div className="w-full flex flex-col gap-3">
      {/* 360° Photosphere Simulation Viewport Stage */}
      <div 
        ref={stageWrapperRef}
        className={`relative w-full ${
          isFullscreen 
            ? 'fixed inset-0 z-50 h-screen w-screen rounded-none border-0' 
            : 'h-[52vh] min-h-[360px] max-h-[580px] sm:h-auto sm:aspect-video rounded-2xl'
        } overflow-hidden bg-slate-950 border-2 border-slate-300 dark:border-slate-800 shadow-2xl select-none group`}
      >
      {/* Three.js Canvas Container */}
      <div
        ref={containerRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={handleStageClick}
        className={`w-full h-full relative touch-none ${
          isCalibrating ? 'cursor-crosshair' : 'cursor-grab active:cursor-grabbing'
        }`}
      >
        <div ref={canvasMountRef} className="w-full h-full block" />

        {/* Texture or WebGL Error Banner with 2D Fallback Action */}
        {textureError && (
          <div className="absolute inset-0 bg-slate-950/90 backdrop-blur-md flex flex-col items-center justify-center p-6 text-center gap-3 z-30 pointer-events-auto">
            <div className="w-12 h-12 rounded-2xl bg-amber-500/20 text-amber-400 border border-amber-500/30 flex items-center justify-center shadow-lg">
              <Info className="w-6 h-6" />
            </div>
            <div className="max-w-md space-y-1">
              <h4 className="text-sm font-bold text-white">Photosphere Simulation Display Notice</h4>
              <p className="text-xs text-slate-300 leading-relaxed">{textureError}</p>
            </div>
            {onFallbackTo2D && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleStopTour();
                  onFallbackTo2D();
                }}
                className="mt-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-xs font-bold shadow-lg transition-all flex items-center gap-2 cursor-pointer active:scale-95"
              >
                <span>Switch to 2D Canvas</span>
              </button>
            )}
          </div>
        )}

        {/* Texture Loading Overlay */}
        {isLoadingTexture && !textureError && (
          <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-xs flex flex-col items-center justify-center gap-3 z-30 pointer-events-none">
            <div className="w-8 h-8 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" />
            <span className="text-xs font-semibold text-slate-300">
              Loading 360° Photosphere Environment...
            </span>
          </div>
        )}

        {/* 3D Hotspot Pins Projected on 2D Screen */}
        {projectedHotspots.map(({ hotspot: hs, screenX, screenY, isVisible, isInFront }) => {
          if (!isVisible) return null;

          const isSelected = activeHotspotId === hs.id;
          const isBeingCalibrated = isCalibrating && calibratingHotspotId === hs.id;
          const isTourFocus = isTourActive && tourSteps[tourStepIndex]?.hotspot.id === hs.id;
          const hsActions = hs.availableActions || [];
          const hasCompletedAction = hsActions.some(act => 
            completedActionIds.includes(act.actionId)
          );

          return (
            <div
              key={hs.id}
              style={{
                left: `${screenX}px`,
                top: `${screenY}px`,
                transform: 'translate(-50%, -50%)'
              }}
              onClick={(e) => {
                e.stopPropagation();
                lookAtHotspot(hs);
                onSelectHotspot(hs);
              }}
              className={`absolute z-20 flex flex-col items-center cursor-pointer transition-transform duration-150 hover:scale-115 p-2 -m-2 touch-manipulation min-w-[44px] min-h-[44px] justify-center ${
                isTourFocus ? 'scale-125 z-40' : isSelected ? 'scale-120 z-30' : ''
              }`}
            >
              {/* Outer Pulse Ring */}
              <div className={`relative flex items-center justify-center ${
                isTourFocus
                  ? 'w-11 h-11'
                  : isBeingCalibrated 
                  ? 'w-10 h-10' 
                  : isSelected 
                  ? 'w-9 h-9' 
                  : 'w-7 h-7'
              }`}>
                <div className={`absolute inset-0 rounded-full animate-ping opacity-75 ${
                  isTourFocus
                    ? 'bg-emerald-400'
                    : isBeingCalibrated
                    ? 'bg-amber-400'
                    : hasCompletedAction
                    ? 'bg-emerald-400'
                    : isSelected
                    ? 'bg-blue-400'
                    : 'bg-white'
                }`} />

                <div className={`relative w-full h-full rounded-full shadow-lg flex items-center justify-center border-2 border-white dark:border-slate-900 transition-colors ${
                  isTourFocus
                    ? 'bg-emerald-500 text-slate-950 font-black ring-4 ring-emerald-400/60 shadow-xl'
                    : isBeingCalibrated
                    ? 'bg-amber-500 text-slate-950 ring-4 ring-amber-400/40'
                    : hasCompletedAction
                    ? 'bg-emerald-500 text-white'
                    : isSelected
                    ? 'bg-blue-600 text-white ring-4 ring-blue-500/40'
                    : 'bg-slate-900/90 text-white hover:bg-blue-600'
                }`}>
                  <span className="text-[11px] font-black">
                    {isTourFocus ? `★` : (hs.name || 'S').slice(0, 1)}
                  </span>
                </div>
              </div>

              {/* Station Label Badge */}
              <div className={`mt-1 px-2 py-0.5 rounded-full text-[9px] sm:text-[10px] font-bold shadow-md whitespace-nowrap transition-all border flex items-center gap-1 ${
                isTourFocus
                  ? 'bg-emerald-600 text-white border-emerald-300 ring-2 ring-emerald-400/60'
                  : isSelected
                  ? 'bg-blue-600 text-white border-blue-400 ring-2 ring-blue-400/40'
                  : isBeingCalibrated
                  ? 'bg-amber-500 text-slate-950 border-amber-300'
                  : hasCompletedAction
                  ? 'bg-emerald-600/90 text-white border-emerald-400'
                  : 'bg-slate-900/80 backdrop-blur-xs text-slate-200 border-white/20 hover:bg-slate-900 hover:text-white'
              }`}>
                <span>{hs.name || 'Station'}</span>
                <span className={`text-[8.5px] sm:text-[9px] px-1 py-0.2 rounded-full font-mono ${
                  isTourFocus
                    ? 'bg-emerald-800 text-emerald-100 font-bold'
                    : isSelected 
                    ? 'bg-blue-800 text-white' 
                    : hsActions.length > 0 
                    ? 'bg-white/20 text-slate-200' 
                    : 'bg-rose-500/80 text-white'
                }`}>
                  {hsActions.length} {hsActions.length === 1 ? 'step' : 'steps'}
                </span>
              </div>
            </div>
          );
        })}

        {/* Off-Screen Directional Indicators (Guides student to turn around if stations are behind them) */}
        {projectedHotspots.filter(p => !p.isVisible).map(({ hotspot: hs, isInFront, angleFromCenter }) => {
          return null; // Keep view pristine, mini-radar below handles full 360 spatial guidance
        })}
      </div>

      {/* Top Floating Telemetry & Mode Bar */}
      <div className="absolute top-2.5 sm:top-3 left-2.5 sm:left-3 right-2.5 sm:right-3 flex items-center justify-between gap-2 pointer-events-none z-20 flex-wrap">
        <div className="flex items-center gap-1.5 sm:gap-2 pointer-events-auto flex-wrap">
          {/* PLAY SKILL GUIDED TOUR BUTTON - Prominent High-Visibility Primary Action */}
          {!isTourActive && tourSteps.length > 0 && (
            <button
              id="play-skill-tour-btn"
              type="button"
              onClick={handleStartTour}
              className={`relative group px-3 py-1.5 sm:px-4 sm:py-2 rounded-xl ${theme.primaryBtn} text-white font-black text-[11px] sm:text-xs shadow-xl shadow-black/50 ring-2 ring-white/70 hover:ring-white flex items-center gap-1.5 sm:gap-2 transition-all duration-200 hover:scale-105 active:scale-95 cursor-pointer`}
              title="Start 360° auto-pan walkthrough with audio step read-aloud"
            >
              <span className="absolute -inset-0.5 rounded-xl bg-white/20 blur-xs group-hover:blur-sm animate-pulse -z-10" />
              <span className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-white text-slate-900 flex items-center justify-center shrink-0 shadow-sm group-hover:scale-110 transition-transform">
                <Play className="w-2 sm:w-2.5 h-2 sm:h-2.5 fill-current ml-0.5" />
              </span>
              <span className="font-extrabold tracking-tight whitespace-nowrap">Play Skill Tour</span>
              <span className="px-1.5 py-0.5 rounded-md bg-black/30 text-white font-mono text-[9px] sm:text-[10px] font-bold tracking-wider uppercase border border-white/20 whitespace-nowrap">
                {tourSteps.length} Steps
              </span>
            </button>
          )}

          {/* PREVIEW TOUR BUTTON WITH MINI THUMBNAIL & INTERACTIVE VISUAL TOOLTIP */}
          {!isTourActive && tourSteps.length > 0 && (
            <div className="relative pointer-events-auto" ref={tourPreviewRef}>
              <button
                id="preview-skill-tour-btn"
                type="button"
                onClick={() => setShowTourPreview(prev => !prev)}
                className="px-2 sm:px-2.5 py-1 sm:py-1.5 rounded-xl bg-slate-900/85 hover:bg-slate-800/95 backdrop-blur-md border border-white/20 text-white shadow-xl flex items-center gap-1.5 transition-all duration-200 hover:scale-105 active:scale-95 cursor-pointer group"
                title="Preview Tour stations & visual itinerary"
                aria-label="Preview Tour stations"
              >
                {/* Mini Photo Thumbnail */}
                <div className="relative w-5 h-5 sm:w-6 sm:h-6 rounded-lg overflow-hidden border border-white/40 shrink-0 shadow-xs group-hover:border-emerald-400 transition-colors">
                  <img
                    src={fallbackImageUrl || panoramaUrl}
                    alt="Tour visual preview thumbnail"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-slate-950/30 flex items-center justify-center">
                    <Eye className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white drop-shadow-sm" />
                  </div>
                </div>
                <div className="flex flex-col text-left leading-none pr-0.5">
                  <span className="font-extrabold text-[10px] sm:text-xs whitespace-nowrap text-slate-100 group-hover:text-emerald-300 transition-colors">
                    Preview Tour
                  </span>
                  <span className="text-[7.5px] sm:text-[8px] font-mono font-bold text-slate-400 uppercase tracking-wider mt-0.5">
                    {uniqueStations.length} Stations
                  </span>
                </div>
              </button>

              {/* Rich Visual Tour Preview Popover */}
              {showTourPreview && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 sm:left-0 sm:translate-x-0 mt-2 w-[calc(100vw-1.5rem)] max-w-sm sm:w-96 bg-slate-950/95 backdrop-blur-2xl border-2 border-emerald-400/60 rounded-2xl p-4 shadow-2xl z-40 animate-in fade-in slide-in-from-top-2 duration-200 ring-1 ring-white/10">
                  {/* Popover Header */}
                  <div className="flex items-center justify-between pb-2.5 border-b border-white/10 mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-lg bg-emerald-500/20 text-emerald-300 flex items-center justify-center border border-emerald-400/40">
                        <Eye className="w-3.5 h-3.5" />
                      </div>
                      <div>
                        <h4 className="text-xs font-black text-white uppercase tracking-wider">
                          VR Tour Visual Preview
                        </h4>
                        <p className="text-[10px] text-slate-400 leading-tight">
                          Visual sense of room stations before starting
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowTourPreview(false)}
                      className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
                      title="Close preview"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  {/* High-Fidelity Room Visual Snapshot */}
                  <div className="relative aspect-video rounded-xl overflow-hidden border border-white/20 mb-3 shadow-inner bg-black group/img">
                    <img
                      src={fallbackImageUrl || panoramaUrl}
                      alt="Clinical VR environment preview"
                      className="w-full h-full object-cover group-hover/img:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-transparent to-transparent flex flex-col justify-end p-2.5">
                      <span className="text-[10px] font-mono font-bold text-emerald-300 uppercase tracking-widest flex items-center gap-1">
                        <Sparkles className="w-3 h-3 text-emerald-400" /> 360° VR Spherical Environment
                      </span>
                      <span className="text-xs font-black text-white truncate drop-shadow-md">
                        {environmentName || 'Long-Term Care Clinical Environment'}
                      </span>
                    </div>
                  </div>

                  {/* Tour Itinerary / Station Sequence Preview */}
                  <div className="mb-3.5">
                    <div className="flex items-center justify-between text-[10px] font-bold text-slate-400 mb-1.5 uppercase tracking-wider">
                      <span>Guided Route ({uniqueStations.length} Stations • {tourSteps.length} Steps)</span>
                      <span className="text-emerald-400 font-mono">Audio Narration</span>
                    </div>
                    <div className="space-y-1.5 max-h-36 overflow-y-auto pr-1 scrollbar-thin">
                      {uniqueStations.map((st, idx) => (
                        <div
                          key={st.id}
                          className="flex items-center justify-between p-2 rounded-xl bg-slate-900/80 border border-white/10 text-xs hover:border-emerald-500/40 transition-colors"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-400/40 text-[10px] font-mono font-bold flex items-center justify-center shrink-0">
                              {idx + 1}
                            </span>
                            <span className="text-xs font-bold text-white truncate">
                              {st.name}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            {st.isCritical && (
                              <span className="px-1.5 py-0.5 rounded bg-amber-400/20 text-amber-300 border border-amber-400/40 text-[9px] font-bold">
                                Critical
                              </span>
                            )}
                            <span className="text-[10px] font-mono text-slate-400">
                              {st.stepCount} {st.stepCount === 1 ? 'step' : 'steps'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Action Footer */}
                  <div className="pt-2.5 border-t border-white/10 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setShowTourPreview(false);
                        handleStartTour();
                      }}
                      className={`w-full py-2.5 rounded-xl ${theme.primaryBtn} text-white font-black text-xs flex items-center justify-center gap-2 shadow-lg active:scale-95 transition-all cursor-pointer`}
                    >
                      <Play className="w-3.5 h-3.5 fill-current" />
                      <span>Start VR Tour Now</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {isTourActive && (
            <div className="px-2.5 sm:px-3.5 py-1.5 sm:py-2 rounded-xl bg-emerald-500 text-slate-950 font-black text-[11px] sm:text-xs shadow-xl flex items-center gap-1.5 border border-white/40 animate-pulse">
              <Headphones className="w-3.5 h-3.5 shrink-0" />
              <span>Tour: {tourStepIndex + 1}/{tourSteps.length}</span>
            </div>
          )}

          {/* Compass & Heading Badge */}
          <div className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-xl bg-slate-900/80 backdrop-blur-md border border-white/15 text-white text-[10px] sm:text-xs font-semibold shadow-lg">
            <Compass className="w-3 sm:w-3.5 h-3 sm:h-3.5 text-blue-400 animate-spin-slow" />
            <span className="font-mono text-[10px] sm:text-[11px] font-bold">
              {currentHeading >= 0 ? `+${currentHeading}°` : `${currentHeading}°`} Yaw
            </span>
            <span className="text-[9px] sm:text-[10px] text-slate-400 font-mono hidden xs:inline">
              ({currentPitch >= 0 ? `+${currentPitch}°` : `${currentPitch}°`} Pitch)
            </span>
          </div>

          <div className="hidden md:flex px-2.5 py-1.5 rounded-xl bg-purple-500/20 backdrop-blur-md border border-purple-400/40 text-purple-200 text-[10px] font-bold shadow-sm items-center gap-1.5">
            <Sparkles className="w-3 h-3 text-purple-300" />
            <span>360° Photosphere</span>
          </div>
        </div>

        {/* Super Admin Live Calibration Indicator */}
        {isCalibrating && (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-amber-500/90 text-slate-950 font-bold text-xs shadow-lg animate-pulse border border-white/30 pointer-events-auto">
            <Crosshair className="w-3.5 h-3.5" />
            <span>Click 360 Room to Calibrate Active Pin</span>
          </div>
        )}

        {/* Quick Orientation Buttons */}
        <div className="hidden xs:flex items-center gap-0.5 sm:gap-1 pointer-events-auto bg-slate-900/80 backdrop-blur-md p-0.5 sm:p-1 rounded-xl border border-white/15 shadow-lg">
          <button
            type="button"
            onClick={() => rotateToAngle(0, 0)}
            className="px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-lg text-[9px] sm:text-[10px] font-bold text-slate-300 hover:text-white hover:bg-white/10 transition-colors"
            title="Face Center / Front View (0°)"
          >
            0°
          </button>
          <button
            type="button"
            onClick={() => rotateToAngle(-90, 0)}
            className="px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-lg text-[9px] sm:text-[10px] font-bold text-slate-300 hover:text-white hover:bg-white/10 transition-colors"
            title="Face Left Wall (-90°)"
          >
            -90°
          </button>
          <button
            type="button"
            onClick={() => rotateToAngle(90, 0)}
            className="px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-lg text-[9px] sm:text-[10px] font-bold text-slate-300 hover:text-white hover:bg-white/10 transition-colors"
            title="Face Right Wall (+90°)"
          >
            +90°
          </button>
          <button
            type="button"
            onClick={() => rotateToAngle(180, 0)}
            className="px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-lg text-[9px] sm:text-[10px] font-bold text-slate-300 hover:text-white hover:bg-white/10 transition-colors"
            title="Face Back (180°)"
          >
            180°
          </button>
        </div>
      </div>

      {/* Interactive Discovery Banner for First-Time / Returning Students */}
      {!isTourActive && tourSteps.length > 0 && showTourDiscoveryPill && (
        <div className="absolute top-14 sm:top-18 left-1/2 -translate-x-1/2 z-20 pointer-events-auto w-[calc(100%-1.5rem)] max-w-xl mx-auto animate-in fade-in slide-in-from-top-3 duration-300">
          <div className="w-full bg-slate-950/92 hover:bg-slate-950/98 backdrop-blur-xl border-2 border-emerald-400/60 shadow-2xl shadow-black/90 rounded-2xl p-2.5 sm:p-3 flex items-center justify-between gap-2.5 text-white transition-all ring-1 ring-white/15">
            <div className="flex items-center gap-2.5 min-w-0 flex-1">
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-tr from-emerald-500 to-teal-400 flex items-center justify-center shadow-lg shadow-emerald-500/30 text-slate-950 shrink-0">
                <Headphones className="w-4 h-4 sm:w-5 sm:h-5 animate-pulse" />
              </div>
              <div className="text-left min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs sm:text-sm font-black text-white tracking-tight">
                    Guided Skill Tour Available
                  </span>
                  <span className="px-1.5 py-0.2 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-400/40 text-[9px] sm:text-[10px] font-mono font-bold uppercase shrink-0">
                    {tourSteps.length} Audio Steps
                  </span>
                </div>
                <p className="text-[10px] sm:text-xs text-slate-300 leading-snug mt-0.5 line-clamp-1 sm:line-clamp-none">
                  Hands-free auto-pan walkthrough with audio step-by-step narration.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1 sm:gap-2 shrink-0">
              <button
                type="button"
                onClick={handleStartTour}
                className="px-3 py-1.5 sm:px-4 sm:py-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-[11px] sm:text-xs flex items-center gap-1.5 shadow-md active:scale-95 transition-all cursor-pointer whitespace-nowrap"
              >
                <Play className="w-3 h-3 fill-current" />
                <span>Start Tour</span>
              </button>
              <button
                type="button"
                onClick={() => setShowTourDiscoveryPill(false)}
                className="p-1 sm:p-1.5 text-slate-400 hover:text-white rounded-xl hover:bg-white/10 transition-colors cursor-pointer shrink-0"
                title="Dismiss banner"
                aria-label="Dismiss banner"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bottom Floating Control Dock */}
      <div className="absolute bottom-2.5 sm:bottom-3 left-2.5 sm:left-3 right-2.5 sm:right-3 flex items-center justify-between gap-1.5 sm:gap-2 pointer-events-none z-20">
        {/* Spatial Mini-Radar / 360 Station Horizon */}
        <div className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-xl bg-slate-900/85 backdrop-blur-md border border-white/15 text-white text-xs shadow-lg pointer-events-auto min-w-0 max-w-[48vw] sm:max-w-none">
          <span className="text-[9px] sm:text-[10px] font-bold text-slate-400 uppercase tracking-wider shrink-0 hidden xs:inline">
            Stations:
          </span>
          <div className="flex items-center gap-1 overflow-x-auto scrollbar-none py-0.5">
            {(hotspots || []).map((hs) => {
              const coords = hs.sphericalCoordinates || coords2DtoSpherical(
                hs.coordinates?.x ?? 50, 
                hs.coordinates?.y ?? 50
              );
              const isSelected = activeHotspotId === hs.id;
              return (
                <button
                  key={hs.id}
                  type="button"
                  onClick={() => {
                    lookAtHotspot(hs);
                    onSelectHotspot(hs);
                  }}
                  className={`px-2 py-1 rounded-md text-[9px] sm:text-[10px] font-bold transition-colors cursor-pointer border shrink-0 min-h-[28px] flex items-center justify-center ${
                    isSelected
                      ? 'bg-blue-600 text-white border-blue-400 ring-2 ring-blue-400/40'
                      : 'bg-white/10 text-slate-300 border-white/10 hover:bg-white/20'
                  }`}
                  title={`Look at ${hs.name} (${coords.yaw}°)`}
                >
                  {hs.name.split(' ')[0]}
                </button>
              );
            })}
          </div>
        </div>

        {/* Camera Controls Dock (Gyro Look, Auto-Pan, Zoom, Fullscreen, Reset) */}
        <div className="flex items-center gap-0.5 sm:gap-1 pointer-events-auto bg-slate-900/85 backdrop-blur-md p-1 rounded-xl border border-white/15 shadow-lg shrink-0">
          {/* Gyroscope Motion Look (Mobile VR) */}
          {gyroSupported && (
            <button
              type="button"
              onClick={toggleGyro}
              className={`p-1.5 sm:p-2 rounded-lg text-xs font-bold transition-colors flex items-center gap-1 cursor-pointer min-w-[32px] min-h-[32px] justify-center ${
                isGyroActive 
                  ? 'bg-purple-600 text-white ring-2 ring-purple-400' 
                  : 'text-slate-300 hover:text-white hover:bg-white/10'
              }`}
              title={isGyroActive ? "Turn off Gyroscope Look" : "Turn on Gyroscope Motion Look (Move phone to look around)"}
            >
              <Smartphone className="w-3.5 h-3.5" />
              <span className="text-[10px] hidden md:inline">Gyro</span>
            </button>
          )}

          <button
            type="button"
            onClick={toggleAutoRotate}
            className={`p-1.5 sm:p-2 rounded-lg text-xs font-bold transition-colors flex items-center gap-1 min-w-[32px] min-h-[32px] justify-center cursor-pointer ${
              isAutoRotating 
                ? 'bg-blue-600 text-white' 
                : 'text-slate-300 hover:text-white hover:bg-white/10'
            }`}
            title={isAutoRotating ? 'Pause auto-rotation' : 'Start slow 360° cinematic rotation'}
          >
            {isAutoRotating ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
            <span className="text-[10px] hidden lg:inline">Auto-Pan</span>
          </button>

          <div className="w-[1px] h-4 bg-white/20 my-auto hidden xs:block" />

          <button
            type="button"
            onClick={() => {
              cameraAnglesRef.current.targetFov = Math.max(40, cameraAnglesRef.current.targetFov - 10);
            }}
            className="p-1.5 sm:p-2 rounded-lg text-slate-300 hover:text-white hover:bg-white/10 transition-colors min-w-[32px] min-h-[32px] flex items-center justify-center cursor-pointer hidden xs:flex"
            title="Zoom In"
          >
            <ZoomIn className="w-3.5 h-3.5" />
          </button>

          <button
            type="button"
            onClick={() => {
              cameraAnglesRef.current.targetFov = Math.min(85, cameraAnglesRef.current.targetFov + 10);
            }}
            className="p-1.5 sm:p-2 rounded-lg text-slate-300 hover:text-white hover:bg-white/10 transition-colors min-w-[32px] min-h-[32px] flex items-center justify-center cursor-pointer hidden xs:flex"
            title="Zoom Out"
          >
            <ZoomOut className="w-3.5 h-3.5" />
          </button>

          <button
            type="button"
            onClick={() => {
              rotateToAngle(initialHeading, 0);
              cameraAnglesRef.current.targetFov = 75;
            }}
            className="p-1.5 sm:p-2 rounded-lg text-slate-300 hover:text-white hover:bg-white/10 transition-colors min-w-[32px] min-h-[32px] flex items-center justify-center cursor-pointer"
            title="Reset View & Zoom"
          >
            <RotateCcw className="w-3.5 h-3.5" />
          </button>

          {/* Fullscreen Mode */}
          <button
            type="button"
            onClick={toggleFullscreen}
            className="p-1.5 sm:p-2 rounded-lg text-slate-300 hover:text-white hover:bg-white/10 transition-colors min-w-[32px] min-h-[32px] flex items-center justify-center cursor-pointer"
            title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen VR"}
          >
            {isFullscreen ? <Minimize className="w-3.5 h-3.5" /> : <Maximize className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {/* Super Admin Live Nudge Controls (when calibrating) */}
      {isCalibrating && calibratingHotspotId && onNudgePin && (
        <div className="absolute top-16 right-3 bg-slate-900/90 backdrop-blur-md p-2 rounded-xl border border-amber-400/40 shadow-xl flex flex-col items-center gap-1.5 z-20">
          <span className="text-[10px] font-bold text-amber-300 uppercase">
            3D Nudge Pin
          </span>
          <div className="grid grid-cols-3 gap-1 w-24">
            <div />
            <button
              type="button"
              onClick={() => onNudgePin(0, 3)}
              className="p-1 rounded bg-white/10 hover:bg-amber-500 hover:text-slate-950 text-white flex items-center justify-center"
              title="Nudge Pitch Up (+3°)"
            >
              <ArrowUp className="w-3.5 h-3.5" />
            </button>
            <div />
            <button
              type="button"
              onClick={() => onNudgePin(-3, 0)}
              className="p-1 rounded bg-white/10 hover:bg-amber-500 hover:text-slate-950 text-white flex items-center justify-center"
              title="Nudge Yaw Left (-3°)"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={() => rotateToAngle(0, 0)}
              className="p-1 rounded bg-white/10 hover:bg-white/20 text-white flex items-center justify-center text-[9px] font-bold"
              title="Center View"
            >
              0°
            </button>
            <button
              type="button"
              onClick={() => onNudgePin(3, 0)}
              className="p-1 rounded bg-white/10 hover:bg-amber-500 hover:text-slate-950 text-white flex items-center justify-center"
              title="Nudge Yaw Right (+3°)"
            >
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
            <div />
            <button
              type="button"
              onClick={() => onNudgePin(0, -3)}
              className="p-1 rounded bg-white/10 hover:bg-amber-500 hover:text-slate-950 text-white flex items-center justify-center"
              title="Nudge Pitch Down (-3°)"
            >
              <ArrowDown className="w-3.5 h-3.5" />
            </button>
            <div />
          </div>
        </div>
      )}

      {/* Helpful Hint Overlay for New Students */}
      {!isTourActive && (
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 bg-slate-900/70 backdrop-blur-xs px-3 py-1 rounded-full text-[10px] text-slate-300 pointer-events-none opacity-80 group-hover:opacity-100 transition-opacity">
          Drag to look around in 360° • Scroll / Pinch to zoom • Click station pin to interact
        </div>
      )}
    </div>

    {/* STATION QUICK ACTION SHEET FOR MOBILE & DESKTOP (When free-looking in 360 and a station is selected) */}
    {!isTourActive && activeHotspotId && (() => {
      const selectedHotspot = (hotspots || []).find(h => h.id === activeHotspotId);
      if (!selectedHotspot || !selectedHotspot.availableActions || selectedHotspot.availableActions.length === 0) return null;

      return (
        <div 
          id="photosphere-station-action-sheet" 
          className="w-full bg-slate-900/95 dark:bg-slate-950/95 border-2 border-blue-500/60 rounded-2xl p-3.5 sm:p-4 shadow-xl flex flex-col gap-3 animate-in fade-in slide-in-from-top-2 duration-300"
        >
          <div className="flex items-center justify-between pb-2.5 border-b border-white/10 gap-2">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-8 h-8 rounded-xl bg-blue-500/20 text-blue-400 border border-blue-400/40 flex items-center justify-center shrink-0">
                <Compass className="w-4 h-4" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[9px] sm:text-[10px] font-mono font-bold text-blue-400 uppercase tracking-widest">
                    Active Station
                  </span>
                  <span className="px-1.5 py-0.2 rounded-full bg-blue-600/30 text-blue-300 text-[9px] font-mono font-bold">
                    {selectedHotspot.availableActions.length} Actions
                  </span>
                </div>
                <h4 className="text-sm font-black text-white truncate">
                  {selectedHotspot.name}
                </h4>
              </div>
            </div>

            <button
              type="button"
              onClick={() => onSelectHotspot({} as any)}
              className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors cursor-pointer shrink-0"
              title="Close station actions"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* List of Actions for this Station */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-h-56 overflow-y-auto pr-1 scrollbar-thin">
            {selectedHotspot.availableActions.map(action => {
              const isDone = completedActionIds.includes(action.actionId);
              return (
                <div
                  key={action.actionId}
                  className={`p-3 rounded-xl border flex items-center justify-between gap-2.5 transition-all ${
                    isDone
                      ? 'bg-emerald-950/30 border-emerald-500/40 text-emerald-200'
                      : 'bg-slate-800/80 hover:bg-slate-800 border-white/10 text-white'
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-xs font-bold leading-tight">
                        {action.label}
                      </span>
                      {action.isCritical && (
                        <span className="px-1.5 py-0.2 rounded bg-amber-400/20 text-amber-300 border border-amber-400/40 text-[8.5px] font-bold uppercase tracking-wider shrink-0">
                          Critical
                        </span>
                      )}
                    </div>
                    <span className="text-[10px] text-slate-400 block mt-0.5">
                      +{action.pointsAwarded} pts • {action.category}
                    </span>
                  </div>

                  {onExecuteAction && (
                    <button
                      type="button"
                      onClick={() => onExecuteAction(action)}
                      disabled={isDone}
                      className={`px-3 py-2 rounded-lg text-xs font-extrabold shrink-0 flex items-center gap-1.5 transition-all cursor-pointer min-h-[36px] ${
                        isDone
                          ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 cursor-default'
                          : `${theme.primaryBtn} text-white shadow-xs active:scale-95`
                      }`}
                    >
                      {isDone ? (
                        <>
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                          <span>Done</span>
                        </>
                      ) : (
                        <span>Execute</span>
                      )}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    })()}

    {/* GUIDED SKILL TOUR DEDICATED CONSOLE DOCK (Positioned directly below the 360° stage for 100% unobstructed image & fixture visibility) */}
    {isTourActive && tourSteps[tourStepIndex] && (() => {
      const currentStep = tourSteps[tourStepIndex];
      const isCurrentStepDone = completedActionIds.includes(currentStep.action.actionId);

      return (
        <div className="w-full bg-slate-900/95 dark:bg-slate-950/95 border-2 border-emerald-500/60 rounded-2xl p-4 shadow-xl flex flex-col gap-3 animate-in fade-in slide-in-from-top-2 duration-300">
          {/* Top Bar of Tour Card: Step number, station name, audio controls */}
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-2.5 py-1 rounded-full bg-emerald-500 text-slate-950 font-black text-xs uppercase tracking-wider shadow-xs flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5" />
                Step {currentStep.stepNumber} of {tourSteps.length}
              </span>
              <span className="text-xs sm:text-sm font-black text-white flex items-center gap-1.5">
                Station: <span className="text-emerald-400 font-extrabold">{currentStep.hotspot.name}</span>
              </span>
              {currentStep.isCritical && (
                <span className="px-2.5 py-0.5 rounded-full bg-amber-400 text-slate-950 font-black text-[10px] uppercase tracking-wider shadow-xs">
                  Critical Safety Step
                </span>
              )}
            </div>

            {/* Media & Audio Controls */}
            <div className="flex items-center gap-1 bg-slate-800/90 dark:bg-slate-900/90 rounded-xl p-1 border border-white/10">
              <button
                type="button"
                onClick={handlePrevTourStep}
                disabled={tourStepIndex === 0}
                className="p-1.5 rounded-lg text-slate-300 hover:text-white hover:bg-white/10 disabled:opacity-25 disabled:hover:bg-transparent transition-colors cursor-pointer"
                title="Previous Step"
              >
                <SkipBack className="w-4 h-4" />
              </button>

              <button
                type="button"
                onClick={handlePauseResumeTour}
                className="p-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold transition-all cursor-pointer shadow-xs active:scale-95"
                title={isTourPaused ? "Resume Walkthrough" : "Pause Walkthrough"}
              >
                {isTourPaused ? <Play className="w-4 h-4 fill-current" /> : <Pause className="w-4 h-4" />}
              </button>

              <button
                type="button"
                onClick={handleNextTourStep}
                disabled={tourStepIndex >= tourSteps.length - 1}
                className="p-1.5 rounded-lg text-slate-300 hover:text-white hover:bg-white/10 disabled:opacity-25 disabled:hover:bg-transparent transition-colors cursor-pointer"
                title="Next Step"
              >
                <SkipForward className="w-4 h-4" />
              </button>

              <div className="w-[1px] h-4 bg-white/20 my-auto" />

              {/* Voice Read-Aloud Toggle */}
              <button
                type="button"
                onClick={toggleVoice}
                className={`px-2 py-1.5 rounded-lg text-xs font-bold transition-colors flex items-center gap-1.5 cursor-pointer ${
                  isVoiceEnabled ? 'text-emerald-400 hover:bg-emerald-950/40' : 'text-slate-400 hover:bg-white/10'
                }`}
                title={isVoiceEnabled ? "Mute Voice Read-Aloud" : "Unmute Voice Read-Aloud"}
              >
                {isVoiceEnabled ? (
                  <>
                    <Volume2 className={`w-4 h-4 ${isSpeaking ? 'animate-pulse text-emerald-300' : ''}`} />
                    <span className="text-[11px] hidden sm:inline">{isSpeaking ? 'Speaking...' : 'Audio On'}</span>
                  </>
                ) : (
                  <>
                    <VolumeX className="w-4 h-4 text-slate-400" />
                    <span className="text-[11px] hidden sm:inline">Muted</span>
                  </>
                )}
              </button>

              {/* Speed Toggle */}
              <button
                type="button"
                onClick={() => setTourSpeed(prev => prev === 1 ? 1.25 : 1)}
                className="px-2 py-1 rounded-md text-[11px] font-bold text-slate-300 hover:text-white hover:bg-white/10 transition-colors"
                title="Playback Speed"
              >
                {tourSpeed}x
              </button>

              <div className="w-[1px] h-4 bg-white/20 my-auto" />

              {/* Close Tour */}
              <button
                type="button"
                onClick={handleStopTour}
                className="p-1.5 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-white/10 transition-colors cursor-pointer"
                title="Exit Guided Walkthrough"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Step Action Label & Subtitle Text */}
          <div className="bg-slate-800/80 dark:bg-slate-900/80 rounded-xl p-3 border border-white/10 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex-1">
              <h4 className="text-sm font-black text-white flex items-center gap-2">
                <span>{currentStep.action.label}</span>
                {isSpeaking && (
                  <span className="flex items-center gap-1 text-emerald-400 text-[10px] font-bold animate-pulse">
                    <Volume2 className="w-3.5 h-3.5" /> Reading aloud...
                  </span>
                )}
              </h4>
              <p className="text-xs text-slate-300 mt-1 leading-relaxed">
                {currentStep.feedbackDialogue}
              </p>
            </div>

            {onExecuteAction && (
              <button
                type="button"
                onClick={() => onExecuteAction(currentStep.action)}
                className={`shrink-0 px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-2 shadow-md active:scale-95 transition-all cursor-pointer ${
                  isCurrentStepDone
                    ? 'bg-emerald-600 text-white'
                    : `${theme.primaryBtn} text-white`
                }`}
              >
                <CheckCircle2 className="w-4 h-4" />
                <span>{isCurrentStepDone ? 'Verified ✓' : 'Practice Step'}</span>
              </button>
            )}
          </div>

          {/* Clickable Step Scrubber Dots */}
          <div className="flex items-center gap-1.5 overflow-x-auto pt-0.5 scrollbar-thin">
            {tourSteps.map((s, idx) => {
              const isCurrent = idx === tourStepIndex;
              const isDone = completedActionIds.includes(s.action.actionId);
              return (
                <button
                  key={s.action.actionId || idx}
                  type="button"
                  onClick={() => navigateToTourStep(idx)}
                  className={`h-2.5 rounded-full transition-all cursor-pointer ${
                    isCurrent 
                      ? 'w-8 bg-emerald-400 shadow-xs' 
                      : isDone
                      ? 'w-3.5 bg-emerald-600/80 hover:bg-emerald-500'
                      : 'w-2.5 bg-slate-700 hover:bg-slate-500'
                  }`}
                  title={`Step ${s.stepNumber}: ${s.cleanTitle}`}
                />
              );
            })}
          </div>
        </div>
      );
    })()}
  </div>
);
};
